"use client";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  effectivenessList,
  informationCollectionToolList,
  oftenCollectedList,
} from "../../../utils/types";
import { z } from "zod";
import { formatOptions } from "../../../utils/helpers";
import Select from "react-select";
import axios from "axios";
import toast from "react-hot-toast";
import { useRequestStore } from "../../../data/stores/loggerStore";
const formSchema = z.object({
  toolUsedToCollectData: z.string().min(1, "Tool name is required"),
  informationCollectionTool: z
    .string()
    .min(1, "Technology selection is required"),
  oftenCollected: z.string().min(1, "Data collection frequency is required"),
  dataCollectionToolPurposeServe: z
    .string()
    .min(1, "Tool effectiveness rating is required"),
});

type FormData = z.infer<typeof formSchema>;

const DataCollectionEdit = ({ data, setIsEditing }) => {
  console.log(data, "data");
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      toolUsedToCollectData: data?.toolUsedToCollectData || undefined,
      informationCollectionTool: data?.informationCollectionTool || undefined,
      oftenCollected: data?.oftenCollected || undefined,
      dataCollectionToolPurposeServe:
        data?.dataCollectionToolPurposeServe || undefined,
    },
  });
  const frequency = formatOptions(oftenCollectedList);
  const techOptions = formatOptions(informationCollectionToolList);
  const effectivenessOptions = formatOptions(effectivenessList);
  const { dataSystemAll, getDynamicLogger }: any = useRequestStore();
  //   console.log(getValues, "value");
  const onSubmit = async (itemForm: any) => {
    try {
      const payload = {
        datasystems: [
          {
            toolUsedToCollectData: itemForm.toolUsedToCollectData,
            informationCollectionTool: itemForm.informationCollectionTool,
            oftenCollected: itemForm.oftenCollected,
            dataCollectionToolPurposeServe:
              itemForm.dataCollectionToolPurposeServe,
            erpModule: data.erpModule,
            _id: data?._id,
          },
        ],
        email: data?.profile?.user?.email,
        user: data?.profile?.user?._id,
      };

      const res = await axios.put(`/api/v1/data-system`, payload);
      const updatedArray = res?.data?.data;
      const newDocs = dataSystemAll.docs.map((d: any) => {
        const updated = updatedArray.find((u: any) => u._id === d._id);
        if (!updated) return d;
        return {
          ...d,
          ...updated,
          profile:
            typeof updated.profile === "string" ? d.profile : updated.profile,
        };
      });
      const updatedDataSystemAll = { ...dataSystemAll, docs: newDocs };
      getDynamicLogger(updatedDataSystemAll, "dataSystemAll");
      toast.success(
        res?.data?.message ||
          "Data system updated, Changes would appear shortly."
      );
      // toast.success("Data system updated, Changes would appear shortly.");
      setIsEditing(false);
    } catch (error: any) {
      if (error?.response?.data?.error) {
        error.response.data.error.forEach((err: any) =>
          toast.error(err.message || err.msg)
        );
      } else {
        toast.error(error?.response?.data?.message || "Something went wrong");
      }
    }
  };
  const handleCancel = () => {
    reset({
      toolUsedToCollectData: data?.toolUsedToCollectData || undefined,
      informationCollectionTool: data?.informationCollectionTool || undefined,
      oftenCollected: data?.oftenCollected || undefined,
      dataCollectionToolPurposeServe:
        data?.dataCollectionToolPurposeServe || undefined,
    });
    setIsEditing(false);
  };

  return (
    <div className="font-Inter max-w-2xl">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Name of the tool used to collect data{" "}
            <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register("toolUsedToCollectData")}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.toolUsedToCollectData
                ? "border-red-500"
                : "border-gray-300"
            }`}
            placeholder="E.g. mobile meter reading app"
          />
          {errors.toolUsedToCollectData && (
            <p className="text-red-500 text-xs mt-1">
              {errors.toolUsedToCollectData.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            What technology is used to collect the information{" "}
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="informationCollectionTool"
            control={control}
            rules={{ required: "Please select a technology" }}
            render={({ field }) => (
              <Select
                options={techOptions} // already in { value, label } format
                placeholder="Select Technology..."
                onChange={(selected) => field.onChange(selected?.value)}
                value={
                  techOptions.find((opt) => opt.value === field.value) || null
                }
              />
            )}
          />
          {errors.informationCollectionTool && (
            <p className="text-red-500 text-xs mt-1">
              {errors.informationCollectionTool.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            How often is data collected <span className="text-red-500">*</span>
          </label>
          <Controller
            name="oftenCollected"
            control={control}
            rules={{ required: "Please select a technology" }}
            render={({ field }) => (
              <Select
                options={frequency} // already in { value, label } format
                placeholder="Select Technology..."
                onChange={(selected) => field.onChange(selected?.value)}
                value={
                  frequency.find((opt) => opt.value === field.value) || null
                }
              />
            )}
          />
          {errors.oftenCollected && (
            <p className="text-red-500 text-xs mt-1">
              {errors.oftenCollected.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            How well does the data collection tool serve its purpose?
          </label>
          <Controller
            name="dataCollectionToolPurposeServe"
            control={control}
            rules={{ required: "Please select a technology" }}
            render={({ field }) => (
              <Select
                options={effectivenessOptions} // already in { value, label } format
                placeholder="Select Technology..."
                onChange={(selected) => field.onChange(selected?.value)}
                value={
                  effectivenessOptions.find(
                    (opt) => opt.value === field.value
                  ) || null
                }
              />
            )}
          />
          {errors.dataCollectionToolPurposeServe && (
            <p className="text-red-500 text-xs mt-1">
              {errors.dataCollectionToolPurposeServe.message}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-6">
          <button
            type="button"
            onClick={handleCancel}
            className="px-12 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Go Back
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-12 py-2.5 bg-[#2070B8] font-medium text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DataCollectionEdit;
